/**
 * OnboardingWizard — Asks a freshly-authenticated user for the data we need
 * before they can use the app:
 *
 *   1. Display name (skipped if BA already has one — e.g. Google OAuth).
 *   2. A workspace (organization). User picks the id (slug) themselves —
 *      lowercase alphanumeric + dashes, max 26 chars.
 *
 * Steps are linear; we don't render the org step until name is set.
 *
 * On the final submit we call `/organization/create` (which also flips the
 * session's activeOrganizationId because keepCurrentActiveOrganization
 * defaults to false) and then trigger the parent's `onComplete` so the
 * gate re-fetches and the app proceeds.
 */

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@flowlib/user-auth/ui';
import {
  ORG_ID_MAX_LENGTH,
  checkOrganizationSlug,
  createOrganization,
  setActiveOrganization,
  suggestOrgId,
  updateUserName,
  validateOrgId,
} from './auth-api';

export interface OnboardingWizardProps {
  authApiBase: string;
  /** Called after the org is created and the user is fully onboarded. */
  onComplete: () => void;
}

type Step = 'name' | 'workspace';

export function OnboardingWizard({ authApiBase, onComplete }: OnboardingWizardProps) {
  const { user } = useAuth();

  // BA's `User` has a `name` field. Email/password signups seed it with
  // the email local-part as a placeholder (BA's name column is NOT NULL),
  // so we treat any name that equals the email local-part as "not set yet"
  // and surface the NameStep. Google OAuth populates a real name → skip.
  const rawName = (user?.name ?? '').trim();
  const emailLocalPart = (user?.email ?? '').split('@')[0]?.trim() ?? '';
  const looksLikePlaceholder = rawName.length === 0 || rawName === emailLocalPart;
  const initialName = looksLikePlaceholder ? '' : rawName;
  const [step, setStep] = useState<Step>(initialName ? 'workspace' : 'name');
  const [enteredName, setEnteredName] = useState(initialName);

  const suggestionSource = enteredName || (user?.email ?? '').trim();

  return (
    <div className="flex min-h-screen items-center justify-center bg-fl-background p-4 text-fl-foreground">
      <div className="flex w-full max-w-md flex-col gap-6">
        <StepIndicator step={step} />

        {step === 'name' ? (
          <NameStep
            authApiBase={authApiBase}
            initialName={initialName}
            onSaved={(savedName) => {
              setEnteredName(savedName);
              setStep('workspace');
            }}
          />
        ) : (
          <WorkspaceStep
            authApiBase={authApiBase}
            suggestionSource={suggestionSource}
            onComplete={onComplete}
          />
        )}
      </div>
    </div>
  );
}

function StepIndicator({ step }: { step: Step }) {
  return (
    <div className="flex flex-col items-center gap-2 text-center">
      <div className="flex items-center gap-2">
        <span className="text-2xl font-semibold tracking-tight">Flowlib</span>
        <span className="rounded-md border border-fl-primary/35 bg-fl-primary/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-fl-primary">
          Beta
        </span>
      </div>
      <p className="text-xs uppercase tracking-wide text-fl-muted-foreground">
        {step === 'name' ? 'Step 1 of 2' : 'Step 2 of 2'}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 1: Name
// ─────────────────────────────────────────────────────────────

function NameStep({
  authApiBase,
  initialName,
  onSaved,
}: {
  authApiBase: string;
  initialName: string;
  onSaved: (fullName: string) => void;
}) {
  const [firstName, setFirstName] = useState(() => initialName.split(' ')[0] ?? '');
  const [lastName, setLastName] = useState(() => initialName.split(' ').slice(1).join(' '));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const f = firstName.trim();
    const l = lastName.trim();
    if (!f) {
      setError('First name is required');
      return;
    }
    setBusy(true);
    try {
      const fullName = l ? `${f} ${l}` : f;
      await updateUserName(authApiBase, fullName);
      onSaved(fullName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your name');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="text-center">
        <h1 className="text-lg font-semibold">What should we call you?</h1>
        <p className="mt-1 text-sm text-fl-muted-foreground">
          This shows up on your profile and team activity.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="grid gap-2">
          <label htmlFor="onboarding-first" className="text-sm font-medium leading-none">
            First name
          </label>
          <input
            id="onboarding-first"
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            required
            className="flex h-9 w-full rounded-md border border-fl-border bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-fl-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fl-ring"
          />
        </div>
        <div className="grid gap-2">
          <label htmlFor="onboarding-last" className="text-sm font-medium leading-none">
            Last name
          </label>
          <input
            id="onboarding-last"
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            className="flex h-9 w-full rounded-md border border-fl-border bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-fl-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fl-ring"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-fl-destructive/30 bg-fl-destructive/10 px-3 py-2 text-sm text-fl-destructive">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={busy}
        className="inline-flex h-9 w-full items-center justify-center rounded-md bg-fl-primary px-4 py-2 text-sm font-medium text-fl-primary-foreground shadow transition-colors hover:bg-fl-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fl-ring disabled:pointer-events-none disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Continue'}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────
// Step 2: Workspace (organization)
// ─────────────────────────────────────────────────────────────

function WorkspaceStep({
  authApiBase,
  suggestionSource,
  onComplete,
}: {
  authApiBase: string;
  suggestionSource: string;
  onComplete: () => void;
}) {
  const [orgName, setOrgName] = useState('');
  // Track whether the user has manually edited the id; once they have, we
  // stop syncing it from the org name.
  const [orgIdTouched, setOrgIdTouched] = useState(false);
  const [orgId, setOrgId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

  // Auto-suggest an id from the org name as the user types it (until they
  // edit the id field manually).
  useEffect(() => {
    if (orgIdTouched) {
      return;
    }
    const seed = orgName || suggestionSource;
    setOrgId(suggestOrgId(seed));
  }, [orgName, suggestionSource, orgIdTouched]);

  // Debounced availability check whenever the id changes and is shaped
  // correctly. We don't block submit on this — the create call is the
  // source of truth — but it gives the user fast feedback.
  useEffect(() => {
    const validationError = validateOrgId(orgId);
    if (validationError) {
      setSlugStatus('idle');
      return;
    }
    setSlugStatus('checking');
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const ok = await checkOrganizationSlug(authApiBase, orgId);
        if (!cancelled) {
          setSlugStatus(ok ? 'available' : 'taken');
        }
      } catch {
        if (!cancelled) {
          setSlugStatus('idle');
        }
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [orgId, authApiBase]);

  const idValidationError = useMemo(() => validateOrgId(orgId), [orgId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!orgName.trim()) {
      setError('Workspace name is required');
      return;
    }
    if (idValidationError) {
      setError(idValidationError);
      return;
    }
    if (slugStatus === 'taken') {
      setError('That workspace id is taken — pick another.');
      return;
    }

    setBusy(true);
    try {
      const org = await createOrganization(authApiBase, {
        name: orgName.trim(),
        slug: orgId,
      });
      // BA's /organization/create updates the DB row's activeOrganizationId
      // but the signed session cookie (cookieCache, 5min TTL) still carries
      // the pre-creation value. /organization/set-active is the canonical
      // way to re-mint the cookie so the next request's tenant middleware
      // sees the new org. We then full-reload to flush any in-memory state
      // (the auth provider's session query in particular).
      await setActiveOrganization(authApiBase, org.id);
      onComplete();
      window.location.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create workspace');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="text-center">
        <h1 className="text-lg font-semibold">Create your workspace</h1>
        <p className="mt-1 text-sm text-fl-muted-foreground">
          Workspaces hold your flows, runs, and credentials.
        </p>
      </div>

      <div className="grid gap-2">
        <label htmlFor="onboarding-org-name" className="text-sm font-medium leading-none">
          Workspace name
        </label>
        <input
          id="onboarding-org-name"
          type="text"
          value={orgName}
          onChange={(e) => setOrgName(e.target.value)}
          placeholder="Acme Inc."
          autoComplete="organization"
          required
          className="flex h-9 w-full rounded-md border border-fl-border bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-fl-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fl-ring"
        />
      </div>

      <div className="grid gap-2">
        <label htmlFor="onboarding-org-id" className="text-sm font-medium leading-none">
          Workspace id
        </label>
        <input
          id="onboarding-org-id"
          type="text"
          value={orgId}
          onChange={(e) => {
            setOrgId(e.target.value.toLowerCase());
            setOrgIdTouched(true);
          }}
          placeholder="acme"
          maxLength={ORG_ID_MAX_LENGTH}
          required
          className="flex h-9 w-full rounded-md border border-fl-border bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-fl-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fl-ring"
        />
        <p className="text-xs text-fl-muted-foreground">
          Lowercase letters and numbers only — up to {ORG_ID_MAX_LENGTH} characters. Used in URLs
          and API keys.
        </p>
        <SlugStatusLine orgId={orgId} status={slugStatus} validationError={idValidationError} />
      </div>

      {error && (
        <div className="rounded-md border border-fl-destructive/30 bg-fl-destructive/10 px-3 py-2 text-sm text-fl-destructive">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={busy || slugStatus === 'taken' || Boolean(idValidationError)}
        className="inline-flex h-9 w-full items-center justify-center rounded-md bg-fl-primary px-4 py-2 text-sm font-medium text-fl-primary-foreground shadow transition-colors hover:bg-fl-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-fl-ring disabled:pointer-events-none disabled:opacity-50"
      >
        {busy ? 'Creating workspace…' : 'Create workspace'}
      </button>
    </form>
  );
}

function SlugStatusLine({
  orgId,
  status,
  validationError,
}: {
  orgId: string;
  status: 'idle' | 'checking' | 'available' | 'taken';
  validationError: string | null;
}) {
  if (!orgId) {
    return null;
  }
  if (validationError) {
    return <p className="text-xs text-fl-destructive">{validationError}</p>;
  }
  if (status === 'checking') {
    return <p className="text-xs text-fl-muted-foreground">Checking availability…</p>;
  }
  if (status === 'available') {
    return <p className="text-xs text-emerald-500">{orgId} is available.</p>;
  }
  if (status === 'taken') {
    return <p className="text-xs text-fl-destructive">{orgId} is taken — try another.</p>;
  }
  return null;
}
